import React from "react";
import { SectionTag } from "./ui/SectionTag";
import { motion } from "framer-motion";

type PartnerLogo = {
  id: string;
  name: string;
  file: string; // file name inside `public/assets/logo/`
};

// Same logos as About page (public/assets/logo/)
const PARTNER_LOGOS: PartnerLogo[] = [
  { id: "andhra-bank", name: "Andhra Bank", file: "Andhra_bank.svg 1.webp" },
  { id: "arihant-group", name: "Arihant Group", file: "Arihant-Group-Photoroom 1.webp" },
  { id: "bharti-airtel", name: "Bharti Airtel", file: "Bharti_Airtel-Logo.wine 1.webp" },
  { id: "dena-bank", name: "Dena Bank", file: "Dena_Bank_Logo 1.webp" },
  { id: "havells", name: "Havells", file: "Havells-Logo.wine 1.webp" },
  { id: "hdfc-bank", name: "HDFC Bank", file: "HDFC_Bank_Logo.svg 1.webp" },
  { id: "bajaj-electricals", name: "Bajaj Electricals", file: "kisspng-logo-bajaj-electricals-portable-network-graphics-d-bajaj-electricals-getevangelized-1713903939746-Photoroom 1.webp" },
  { id: "lodha", name: "Lodha", file: "Lodha---New-LOgo 1.webp" },
  { id: "orient-electric", name: "Orient Electric", file: "Orient_Electric_logo.svg 1.webp" },
  { id: "canara-bank", name: "Canara Bank", file: "png-clipart-canara-bank-logo-horizontal-bilingual-bank-logos-Photoroom 1.webp" },
  { id: "sbi", name: "State Bank of India", file: "Sbi_logo.svg-Photoroom 1.webp" },
  { id: "union-bank", name: "Union Bank of India", file: "Union_Bank_of_India_Logo.svg 1.webp" },
  { id: "vodafone", name: "Vodafone", file: "Vodafone_2017_logo.svg 1.webp" },
  { id: "partner-1", name: "Partner", file: "4bc79d18780435.562cf2c77e290-Photoroom 1.webp" },
  { id: "partner-2", name: "Partner", file: "g742.webp" },
  { id: "partner-3", name: "Partner", file: "images 1.webp" },
  { id: "partner-4", name: "Partner", file: "images-Photoroom 1.webp" },
  { id: "partner-5", name: "Partner", file: "path0.webp" },
  { id: "partner-6", name: "Partner", file: "Untitled design 1.webp" },
  { id: "partner-7", name: "Partner", file: "Untitled design (1) 1.webp" },
  { id: "partner-8", name: "Partner", file: "Untitled design (2) 1.webp" },
  { id: "partner-9", name: "Partner", file: "Untitled design (2) 2.webp" },
  { id: "partner-10", name: "Partner", file: "Untitled design (3) 1.webp" },
  { id: "partner-11", name: "Partner", file: "Untitled design (3) 2.webp" },
  { id: "partner-12", name: "Partner", file: "Untitled design (4) 1.webp" },
  { id: "partner-13", name: "Partner", file: "Untitled design (5) 1.webp" },
  { id: "partner-14", name: "Partner", file: "Untitled design (6) 1.webp" },
  { id: "partner-15", name: "Partner", file: "Untitled design (7) 1.webp" },
  { id: "partner-16", name: "Partner", file: "Untitled design (8) 1.webp" },
  { id: "partner-17", name: "Partner", file: "Untitled design (9) 1.webp" },
  { id: "partner-18", name: "Partner", file: "Untitled design (10) 1.webp" },
  { id: "partner-19", name: "Partner", file: "Untitled design (11) 1.webp" },
];

// Reversed order for row 2 so the same logo doesn't align with row 1 at the same time
const PARTNER_LOGOS_ROW2 = [...PARTNER_LOGOS].reverse();

interface PartnerCardProps {
  partner: PartnerLogo;
}

const PartnerCard: React.FC<PartnerCardProps> = ({ partner }) => (
  <div
    className="flex items-center justify-center bg-white rounded-2xl flex-shrink-0 border border-black/5 shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden w-[160px] h-[130px] lg:w-[240px] lg:h-[200px]"
  >
    <div className="w-full h-full p-3 lg:p-4 flex items-center justify-center">
      <img
        src={encodeURI(`/assets/logo/${partner.file}`)}
        alt={partner.name}
        loading="lazy"
        className="w-auto h-[80%] object-contain"
        style={{ filter: "contrast(1.1) saturate(1.05)" }}
      />
    </div>
  </div>
);

export const PartnersSection: React.FC = () => {
  return (
    <section className="bg-white overflow-hidden py-12 lg:py-24">
      <div className="w-[90%] lg:w-[70%] mx-auto mb-12 lg:mb-16">
        <div className="flex flex-col lg:flex-row justify-between items-center lg:items-end">
          <div className="max-w-4xl flex flex-col items-center lg:items-start text-center lg:text-left">
            <SectionTag className="font-raleway">PARTNERS</SectionTag>
            <p
              className="flex flex-col text-2xl md:text-3xl lg:text-heading-large bg-gradient-to-r
         from-[#e20016] from-[0%]
         via-[#712f59] via-[40%]
         to-[#005d9c] to-[100%] bg-clip-text text-transparent font-bold font-open-sans-hebrew py-2"
            >
              <span>Trusted By Leading Brands Across</span> <span className="lg:block">Every Industry</span>
            </p>
            <p className="text-gray-500 text-body max-w-xl font-raleway">
              Discover the brands and partners who rely on Croma Ply for
              reliable quality, durability, and consistent performance.
            </p>
          </div>
        </div>
      </div>

      <div className="w-[90%] lg:w-[70%] mx-auto flex flex-col gap-6">
        {/* Row 1: Right to Left */}
        <div className="w-full overflow-hidden">
          <motion.div
            className="flex gap-6 pl-6"
            initial={{ x: 0 }}
            animate={{ x: "-50%" }}
            transition={{
              repeat: Infinity,
              ease: "linear",
              duration: 280,
            }}
            style={{ width: "max-content" }}
          >
            {[...PARTNER_LOGOS, ...PARTNER_LOGOS, ...PARTNER_LOGOS].map((partner, index) => (
              <PartnerCard
                key={`row1-${index}`}
                partner={partner}
              />
            ))}
          </motion.div>
        </div>

        {/* Row 2: Left to Right */}
        <div className="w-full overflow-hidden">
          <motion.div
            className="flex gap-6 pl-6"
            initial={{ x: "-50%" }}
            animate={{ x: "0%" }}
            transition={{
              repeat: Infinity,
              ease: "linear",
              duration: 280,
            }}
            style={{ width: "max-content" }}
          >
            {[...PARTNER_LOGOS_ROW2, ...PARTNER_LOGOS_ROW2, ...PARTNER_LOGOS_ROW2].map((partner, index) => (
              <PartnerCard
                key={`row2-${index}`}
                partner={partner}
              />
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
